import DIContainer from '@components/DependencyInjectionContainer.js';
/**
 * Mixin to add context menu functionality to a component, such as Grid or InputSelect
 * @param classToExtend
 * @constructor
 */
export function ContextMenuMixin(classToExtend) {
    return class extends classToExtend {
        constructor() {
            super(...arguments);
            this.includeContextMenu = true;
            // private touchStartTimestamp?: number;
            this.LONG_PRESS_DURATION = 1000; // 1 second
            this.MAX_TOUCH_MOVE = 15; // 15 pixels
        }
        /**
         * ContextMenu getter which handles loading the `ContextMenu` dependency.
         */
        get ContextMenu() {
            if (!this.dependencies.ContextMenu) {
                // we didnt receive a request dependency injection, so just use the global singleton
                this.dependencies.ContextMenu = DIContainer.getInstance('ContextMenu');
            }
            return this.dependencies.ContextMenu;
        }
        draw() {
            super.draw();
            this.ContextMenu.register(this);
        }
        /**
         * standard right click handling to display menu at the position of the cursor.
         * @param event
         * @private
         */
        handleRightClick(event) {
            if (!this.includeContextMenu)
                return;
            if (this.longPressTimer) {
                // emulator will trigger the contextmenu event AND the long press, so we will ignore the context menu event if long press is in progress.
                event.preventDefault();
                return;
            }
            const contextInfo = {
                targetElement: event.target,
                x: event.clientX,
                y: event.clientY,
                event: event,
                initiator: this
            };
            let items = this.getContextMenuItems(contextInfo);
            items = this.trigger('getContextMenuItems', {
                defaultEventHandler: (items, _contextInfo) => {
                    return items;
                },
                async: false
            }, items, event);
            if (items && items.length > 0) {
                event.preventDefault();
                this.ContextMenu.showMenu(items, contextInfo, `${this.globalId}_ctx`);
            }
        }
        /**
         * starts a timer when a touch event occurs to see if they hold the touch long enough to display menu
         * ontouchend we reset the timer, so if the menu hasnt displayed by then it was not a long enough press.
         * @param event
         * @private
         */
        handleLongPress(event) {
            if (!this.includeContextMenu)
                return;
            // Clear any previous timer
            if (this.longPressTimer) {
                clearTimeout(this.longPressTimer);
            }
            if (this.touchMoveEventId) {
                this.unRegisterEvent(this.touchMoveEventId);
                this.touchMoveEventId = undefined;
            }
            const touch = event.touches[0];
            this.touchStartX = touch.clientX;
            this.touchStartY = touch.clientY;
            // this.touchStartTimestamp = event.timeStamp;
            // Start a timer to detect long press
            this.longPressTimer = window.setTimeout(() => {
                // if (this.touchStartTimestamp) {
                // const elapsedTime = event.timeStamp - this.touchStartTimestamp;
                // if (elapsedTime >= this.LONG_PRESS_DURATION) {
                const contextInfo = {
                    targetElement: event.target,
                    x: touch.clientX,
                    y: touch.clientY,
                    event: event,
                    initiator: this
                };
                const items = this.getContextMenuItems(contextInfo);
                if (items && items.length > 0) {
                    this.ContextMenu.showMenu(items, contextInfo, `${this.globalId}_ctx`);
                }
                // } 
                // }
            }, this.LONG_PRESS_DURATION);
            this.touchMoveEventId = this.registerEvent('touchmove', document, this.handleTouchMove.bind(this));
        }
        /**
         * Handle touch move event to cancel long press if the user moves more than the allowed distance
         * @param event
         * @private
         */
        handleTouchMove(event) {
            if (this.longPressTimer) {
                const touch = event.touches[0];
                const diffX = Math.abs(touch.clientX - this.touchStartX);
                const diffY = Math.abs(touch.clientY - this.touchStartY);
                if (diffX > this.MAX_TOUCH_MOVE || diffY > this.MAX_TOUCH_MOVE) {
                    clearTimeout(this.longPressTimer);
                    this.longPressTimer = undefined;
                    this.touchStartX = undefined;
                    this.touchStartY = undefined;
                }
            }
        }
        /**
         * reset our timer which is used to detect long touch events
         * @param event
         * @private
         */
        handleTouchEnd(_event) {
            if (this.longPressTimer) {
                clearTimeout(this.longPressTimer);
                this.longPressTimer = undefined;
                this.touchStartX = undefined;
                this.touchStartY = undefined;
                // this.touchStartTimestamp = undefined;
            }
            if (this.touchMoveEventId) {
                this.unRegisterEvent(this.touchMoveEventId);
                this.touchMoveEventId = undefined;
            }
        }
        /**
         * Components that use this mixin would be expected to override this function and return an array of menu items
         * @param contextInfo - provides context about what initiated the context menu
         * @protected
         */
        getContextMenuItems(_contextInfo) {
            return [];
        }
        formDesignerPropertiesGet(properties) {
            properties = super.formDesignerPropertiesGet(properties);
            properties.includeContextMenu = ({
                id: 'includeContextMenu',
                value: this.includeContextMenu,
                componentType: 'InputCheckbox',
                componentProperties: {
                    checkedValue: true,
                    unCheckedValue: false,
                    labelWidth: 'auto'
                },
                captionKey: 'formbuilder_includecontextmenu',
                propertyGroup: '',
                saveFlag: () => !this.includeContextMenu
            });
            return properties;
        }
        /**
         * unregister self from the global `ContextMenu`
         */
        destroy() {
            this.ContextMenu.unregister(this);
            super.destroy();
        }
    };
}
